import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { InstallAppButton } from './InstallAppButton';
import './ChatRail.css';
import './SidebarNav.css';

/** The collapsible sections, in order. Each expands to its full cards; when
 *  collapsed it shows its state-derived "active" items (the panel decides). */
type AccSection = 'workflows' | 'extensions' | 'connectors' | 'files';
type SettingsTab = 'account' | 'billing' | 'usage';

interface Props {
  /** Collapsed = icon-only rail; expanded = labelled nav + accordions + Recent. */
  collapsed: boolean;
  onNewChat(): void;
  onToggleSidebar(): void;
  userName: string | null;
  userEmail: string | null;
  userRole: string | null;
  onOpenSettings(tab: SettingsTab): void;
  onLogout(): void;
  /** Render a section's panel. `expanded` → full cards; otherwise the panel
   *  renders its compact "active items" summary (or nothing). */
  renderSection(section: AccSection, expanded: boolean): ReactNode;
  /** The Recent chat-history panel — always shown below the accordions. */
  renderRecent(): ReactNode;
}

function initials(name: string | null, email: string | null): string {
  const src = (name || email || '').trim();
  return src ? src[0]!.toUpperCase() : '?';
}

// Expanded-sidebar width is user-draggable (see the resizer handle on the
// right boundary). Clamp to the same bounds the base `.sidebar` CSS declares
// (min-width 220 / max-width 520) and persist the last width across sessions.
// The 220 floor is the narrowest width that still fits the "Breww.ai" wordmark
// beside the logo + collapse toggle without clipping it.
const SIDEBAR_MIN = 220;
const SIDEBAR_MAX = 520;
const SIDEBAR_DEFAULT = 226;
const SIDEBAR_WIDTH_KEY = 'brew-sidebar-w';

const ACC_ORDER: AccSection[] = ['workflows', 'extensions', 'connectors', 'files'];
const SECTION_LABEL: Record<AccSection, string> = {
  workflows: 'Workflows',
  extensions: 'Extensions',
  connectors: 'Solutions',
  files: 'Files',
};
const SECTION_ICON: Record<AccSection, ReactNode> = {
  // Workflow — two nodes joined by an L-connector (a flow). Matches "Workflows".
  workflows: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="3" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.7" />
      <rect x="13" y="13" width="8" height="8" rx="2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M7 11v3a3 3 0 0 0 3 3h3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  // Puzzle piece — the universal "extensions / add-ons" glyph. Matches "Extensions".
  extensions: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M19.439 7.85c-.049.322.059.648.289.878l1.568 1.568c.47.47.706 1.087.706 1.704s-.235 1.233-.706 1.704l-1.611 1.611a.98.98 0 0 1-.837.276c-.47-.07-.802-.48-.968-.925a2.501 2.501 0 1 0-3.214 3.214c.446.166.855.497.925.968a.979.979 0 0 1-.276.837l-1.61 1.61a2.404 2.404 0 0 1-1.705.707 2.402 2.402 0 0 1-1.704-.706l-1.568-1.568a1.026 1.026 0 0 0-.877-.29c-.493.074-.84.504-1.02.968a2.5 2.5 0 1 1-3.237-3.237c.464-.18.894-.527.967-1.02a1.026 1.026 0 0 0-.289-.877l-1.568-1.568A2.402 2.402 0 0 1 1.998 12c0-.617.236-1.234.706-1.704L4.23 8.77c.24-.24.581-.353.917-.303.515.077.877.528 1.073 1.01a2.5 2.5 0 1 0 3.259-3.259c-.482-.196-.933-.558-1.01-1.073-.05-.336.062-.676.303-.917l1.525-1.525A2.402 2.402 0 0 1 12 1.998c.617 0 1.234.236 1.704.706l1.568 1.568c.23.23.556.338.877.29.493-.074.84-.504 1.02-.968a2.5 2.5 0 1 1 3.237 3.237c-.464.18-.894.527-.967 1.02Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  ),
  // Lightbulb — reads as "solutions / ideas". Matches the renamed "Solutions".
  connectors: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 18h6M10 22h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  // Folder — the workspace file browser. Matches "Files".
  files: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3 7a2 2 0 0 1 2-2h3.5l2 2H19a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
    </svg>
  ),
};

/**
 * Unified left navigation — accordion model.
 *
 * New chat (action) → Workflows / Extensions / Connectors (each an accordion
 * that expands to its full cards, collapsing to its state-derived active items)
 * → Recent chat history (always shown) → account footer.
 *
 * Collapsed: a slim icon rail (icons only — labels, accordion bodies and Recent
 * are hidden; clicking a section icon expands the sidebar onto that section).
 */
export function SidebarNav({
  collapsed, onNewChat, onToggleSidebar,
  userName, userEmail, onOpenSettings, onLogout,
  renderSection, renderRecent,
}: Props) {
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    const t = document.documentElement.getAttribute('data-theme');
    return t === 'dark' ? 'dark' : 'light';
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('brew-theme', theme); } catch { /* private mode */ }
  }, [theme]);
  function toggleTheme(): void { setTheme((t) => (t === 'dark' ? 'light' : 'dark')); }

  // Which section is open. Toggled by clicking its header (see the group below);
  // the name is historical (it used to open on hover). The active/pinned items
  // stay under the heading regardless of open state.
  const [hoveredSection, setHoveredSection] = useState<AccSection | null>(null);

  // Close any open section immediately — used when the pointer/focus moves to an
  // unrelated row (e.g. New chat) so two rows aren't left highlighted.
  function closeNow(): void {
    setHoveredSection(null);
  }

  // Drag-to-resize the expanded sidebar. Width lives in state (persisted), and
  // is applied inline so it overrides the base .sidebar CSS width; the handle
  // on the right boundary drives it. Collapsed state ignores this (the rail is
  // a fixed 60px), so the handle is only rendered when expanded.
  const [width, setWidth] = useState<number>(() => {
    try {
      const n = parseInt(localStorage.getItem(SIDEBAR_WIDTH_KEY) || '', 10);
      if (Number.isFinite(n)) return Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, n));
    } catch { /* private mode */ }
    return SIDEBAR_DEFAULT;
  });
  useEffect(() => {
    try { localStorage.setItem(SIDEBAR_WIDTH_KEY, String(width)); } catch { /* private mode */ }
  }, [width]);
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);
  function onResizeDown(e: ReactPointerEvent<HTMLDivElement>): void {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startW: width };
    e.currentTarget.setPointerCapture(e.pointerId);
    // Reused legacy class: suppresses the sidebar width transition (so it tracks
    // the cursor without lag) and forces the ew-resize cursor document-wide.
    document.body.classList.add('is-resizing');
  }
  function onResizeMove(e: ReactPointerEvent<HTMLDivElement>): void {
    const d = dragRef.current;
    if (!d) return;
    const next = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, d.startW + (e.clientX - d.startX)));
    setWidth(next);
  }
  function onResizeUp(e: ReactPointerEvent<HTMLDivElement>): void {
    if (!dragRef.current) return;
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* already released */ }
    document.body.classList.remove('is-resizing');
  }

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    function onDocDown(e: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent): void { if (e.key === 'Escape') setMenuOpen(false); }
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <aside
      className="sidebar snav"
      id="sidebar"
      data-collapsed={collapsed || undefined}
      style={collapsed ? undefined : { width, flexBasis: width }}
    >
      <div className="snav__top">
        <button
          type="button"
          className="snav__brand"
          onClick={collapsed ? onToggleSidebar : undefined}
          aria-label={collapsed ? 'Open sidebar' : 'Breww'}
          data-tip="Open sidebar"
          tabIndex={collapsed ? 0 : -1}
        >
          <span className="snav__brand-logo">
          <img src="/brew-logo.png" alt="Breww.ai" />
          </span>
          <span className="snav__brand-toggle" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <rect x="3" y="4" width="18" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
              <path d="M9 4v16" stroke="currentColor" strokeWidth="1.7" />
            </svg>
          </span>
        </button>
        <span className="snav__brand-name"><span className="snav__label-in">Breww.ai</span></span>
        {!collapsed && (
          <button
            type="button"
            className="snav__toggle"
            onClick={onToggleSidebar}
            aria-label="Collapse sidebar"
            aria-pressed={true}
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="4" width="18" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
              <path d="M9 4v16" stroke="currentColor" strokeWidth="1.7" />
              <rect x="3.8" y="4.8" width="4.4" height="14.4" rx="1.6" fill="currentColor" opacity="0.28" />
            </svg>
            <span className="snav__tip">Collapse sidebar</span>
          </button>
        )}
      </div>

        <div className="snav__items">
          <button
            type="button"
            className="snav__item snav__item--newchat"
            onClick={onNewChat}
            onMouseEnter={closeNow}
            data-tip="New chat"
          >
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="snav__label"><span className="snav__label-in">New chat</span></span>
          </button>

          {ACC_ORDER.map((s) => {
            const open = !collapsed && hoveredSection === s;
            return (
              <div
                className="snav__group"
                key={s}
                data-section={s}
                data-open={open || undefined}
              >
                {/* Click-to-open, not hover. Opening a section on mouse-over made
                    the panel flicker as the pointer crossed the list — a section
                    would spring open just from passing over it. The header button
                    toggles it on click; keyboard Enter/Space does the same via
                    onClick, so this stays accessible. Collapsed rail → a click
                    expands the whole sidebar first. */}
                <button
                  type="button"
                  className={`snav__item snav__group-header${open ? ' is-open' : ''}`}
                  data-section={s}
                  data-tip={SECTION_LABEL[s]}
                  onClick={() => {
                    if (collapsed) onToggleSidebar();
                    else setHoveredSection((p) => (p === s ? null : s));
                  }}
                  aria-haspopup="menu"
                  aria-expanded={open}
                >
                  {SECTION_ICON[s]}
                  <span className="snav__label"><span className="snav__label-in">{SECTION_LABEL[s]}</span></span>
                </button>
                <div className="snav__group-body">
                  {renderSection(s, open)}
                </div>
              </div>
            );
          })}
        </div>

      <div className="snav__recent" onMouseEnter={closeNow}>{renderRecent()}</div>

      {/* Only renders when the browser reports the app is installable. */}
      <div className="snav__install" onMouseEnter={closeNow}>
        <InstallAppButton />
      </div>

      <div className="snav__foot" ref={menuRef}>
        <button
          type="button"
          className="snav__profile"
          aria-label="Account menu"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          data-tip={userName || userEmail || 'Account'}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span className="snav__avatar">{initials(userName, userEmail)}</span>
          <span className="snav__profile-meta snav__label">
            <span className="snav__profile-name">{userName || 'You'}</span>
            <span className="snav__profile-email">{userEmail || 'Settings'}</span>
          </span>
        </button>

        {menuOpen && (
          <div className="rail__menu snav__menu" role="menu">
            <div className="rail__menu-head">
              <div className="rail__menu-name">{userName || 'You'}</div>
              {userEmail && <div className="rail__menu-email">{userEmail}</div>}
              {/* <div className="rail__menu-role">Role: {userRole || 'user'}</div> */}
            </div>

            <div className="rail__menu-sep" />

            <button
              type="button"
              role="menuitem"
              className="rail__menu-item"
              onClick={() => { setMenuOpen(false); onOpenSettings('account'); }}
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" stroke="currentColor" strokeWidth="1.6" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Settings</span>
            </button>

            <button
              type="button"
              role="menuitem"
              className="rail__menu-item"
              onClick={toggleTheme}
            >
              {theme === 'dark' ? (
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M20.5 14.5A8.5 8.5 0 0 1 9.5 3.5a8.5 8.5 0 1 0 11 11z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.7" />
                  <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M5.6 18.4l1.8-1.8M16.6 7.4l1.8-1.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
              )}
              <span>Appearance</span>
              <span className="rail__menu-hint">{theme === 'dark' ? 'Dark' : 'Light'}</span>
            </button>

            <div className="rail__menu-sep" />

            <button
              type="button"
              role="menuitem"
              className="rail__menu-item"
              onClick={() => { setMenuOpen(false); onLogout(); }}
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M15 17l5-5-5-5M20 12H9M12 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span>Log out</span>
            </button>
          </div>
        )}
      </div>

      {!collapsed && (
        <div
          className="snav__resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize sidebar"
          data-tip="Drag to resize · double-click to reset"
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
          onDoubleClick={(e) => { e.stopPropagation(); setWidth(SIDEBAR_DEFAULT); }}
        />
      )}
    </aside>
  );
}
